import React from "react";
import { Flex, Heading, Link, Text, VStack, Wrap } from "@chakra-ui/layout";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { HStack, IconButton } from "@chakra-ui/react";
import { QuestionIcon } from "../../styles/icons";
import { useUserContext } from "../../context/UserContext";
import hubIcon from "assets/cherryhub_logo.svg";
import hubIconXmas from "assets/cherryhub_logo_xmas.svg";
import checkIconXmas from "assets/cherrycheck_logo.svg";
import { useBusinessContext } from "../../context/ModelContext";
import { CherryPayApi } from "../../api/models";
import {
  LicencedProduct,
  LicencedProductList,
} from "../../enums/LicencedProduct";
import { DashboardMenu } from "../../components/DashboardMenu/DashboardMenu";

const isDecember = () => new Date().getMonth() === 11;

export type ProductConfig = {
  title: string;
  description: string;
  icon: string;
  cherryPrefixRequired: boolean;
  launchUrl: string | ((businessId: string) => string);
  findOutMoreUrl: string;
};

const productConfigs: Partial<
  Record<CherryPayApi.LicencedProduct, ProductConfig>
> = {
  [LicencedProduct.CherryCheck]: {
    title: "check",
    description:
      "The only realtime, mobile interface capturing compliance reporting anywhere.",
    // Christmas CherryCheck logo only during December; otherwise use the standard hub logo
    icon: isDecember() ? checkIconXmas : hubIcon,
    cherryPrefixRequired: true,
    launchUrl: CHERRYCHECK_BASE_URL,
    findOutMoreUrl: "https://cherryhub.com.au/cherrycheck",
  },
  [LicencedProduct.CherryPay]: {
    title: "pay",
    description:
      "A mobile wallet tailored specifically for the hospitality and entertainment membership venue.",
    icon: isDecember() ? hubIconXmas : hubIcon,
    cherryPrefixRequired: true,
    launchUrl: (businessId: string) => `/businesses/${businessId}/members`,
    findOutMoreUrl: "https://cherryhub.com.au/cherrypay",
  },
  [LicencedProduct.CherryChat]: {
    title: "chat",
    description:
      "A simple, intuitive and affordable marketing CRM platform specifically built for the hospitality and gaming industry, designed to be used by marketers of all skill levels.",
    icon: hubIcon,
    cherryPrefixRequired: true,
    launchUrl: CHERRYCHAT_BASE_URL,
    findOutMoreUrl: "https://cherryhub.com.au/cherrychat",
  },
  [LicencedProduct.Worldpay]: {
    title: "Worldpay for Platforms",
    description:
      "Accept payments seamlessly with Worldpay for Platforms, and access the Portal to view and analyse your transactions.",
    icon: hubIcon,
    cherryPrefixRequired: false,
    launchUrl: "https://portal.worldpayforplatforms.com",
    findOutMoreUrl: "https://go2platforms.worldpay.com/cherryhub",
  },
};

/** Products that have a dashboard / app-switcher card. */
export const isDashboardProduct = (
  product: string
): product is CherryPayApi.LicencedProduct =>
  Object.prototype.hasOwnProperty.call(productConfigs, product);

export const getProductConfig = (
  type: CherryPayApi.LicencedProduct
): ProductConfig | undefined => productConfigs[type];

export const BusinessDashboard = () => {
  const { LicencedCherryHubProducts: products, BusinessId } =
    useBusinessContext();
  const { user } = useUserContext();

  const subscribedProducts = (products ?? []).filter(isDashboardProduct);

  const findoutMoreProducts =
    LicencedProductList()?.filter(
      (product) =>
        isDashboardProduct(product) && products?.indexOf(product) == -1
    ) || [];

  const WrapIconButton = ({
    label,
    url,
    icon,
  }: {
    label: string;
    url: string;
    icon: React.ReactElement;
  }) => {
    const accessibleLabel = label.charAt(0).toUpperCase() + label.slice(1);

    return (
      <Link
        href={url}
        aria-label={accessibleLabel}
        title={accessibleLabel}
        position="relative"
        display="inline-block"
        w="fit-content"
        _hover={{ textDecoration: "none" }}
      >
        <IconButton
          as="span"
          aria-label={accessibleLabel}
          aria-hidden
          tabIndex={-1}
          icon={icon}
          size="xl"
          colorScheme="cherryButton"
          w={24}
          h={24}
          pb={4}
          pointerEvents="none"
        />
        <Text
          as="span"
          fontSize="xs"
          color="white"
          position="absolute"
          bottom={5}
          left="50%"
          transform="translate(-50%, 0)"
          textTransform="uppercase"
        >
          {label}
        </Text>
      </Link>
    );
  };

  return (
    <>
      <PageHeading justifyContent="center" alignItems="center">
        <PageHeading.Title maxW="1200px">
          cherry
          <Text as="span" fontWeight="100">
            hub portal
          </Text>
        </PageHeading.Title>
        <Text w="100%" maxW="1200px">
          Welcome {user?.displayName}
        </Text>
      </PageHeading>
      <Flex w="100%" justifyContent="center">
        <HStack
          w="100%"
          maxW="1200px"
          spacing={8}
          mb={-12}
          mt={-12}
          justifyContent="flex-end"
          pr={24}
        >
          {/* <WrapIconButton
            label="settings"
            url=""
            icon={<SettingsIcon w="100%" h={8} />}
          /> */}

          <WrapIconButton
            label="support"
            url="mailto:support@cherryhub.com.au"
            icon={<QuestionIcon w="100%" h={8} />}
          />
        </HStack>
      </Flex>
      <Flex w="100%" bgColor="white" justifyContent="center">
        <VStack
          px={4}
          py={20}
          w="100%"
          maxW="1200px"
          alignItems="flex-start"
          spacing={0}
        >
          <Heading as="h2" size="lg" color="cherryUi.600">
            Products included your subscription
          </Heading>
          <Text>Select an app to launch</Text>
          <Wrap w="100%" pt={8} spacing={8}>
            {(subscribedProducts.length &&
              subscribedProducts.map((product) => {
                const config = getProductConfig(product)!;
                return (
                  <DashboardMenu
                    key={product}
                    title={config.title}
                    buttonLabel="LAUNCH"
                    description={config.description}
                    icon={config.icon}
                    cherryPrefixRequired={config.cherryPrefixRequired}
                    url={
                      typeof config.launchUrl === "function"
                        ? config.launchUrl(BusinessId)
                        : config.launchUrl
                    }
                  />
                );
              })) || <Text fontStyle="italic">No product available.</Text>}
          </Wrap>
        </VStack>
      </Flex>
      {findoutMoreProducts.length && (
        <Flex w="100%" bgColor="cherryGrey.150" justifyContent="center">
          <VStack
            px={4}
            py={20}
            w="100%"
            maxW="1200px"
            alignItems="flex-start"
            spacing={0}
          >
            <Heading as="h2" size="lg" color="cherryUi.600">
              Products available
            </Heading>
            <Text>
              Products available to help streamline your business - ask us how
            </Text>
            <Wrap w="100%" pt={8} spacing={8}>
              {findoutMoreProducts.map((product) => {
                const config = getProductConfig(product)!;
                return (
                  <DashboardMenu
                    key={product}
                    title={config.title}
                    buttonLabel="FIND OUT MORE"
                    description={config.description}
                    icon={config.icon}
                    cherryPrefixRequired={config.cherryPrefixRequired}
                    url={config.findOutMoreUrl}
                    isExternal={true}
                  />
                );
              }) || <Text fontStyle="italic">No product available.</Text>}
            </Wrap>
          </VStack>
        </Flex>
      )}
    </>
  );
};
